import type { CompanyDetail } from '@planuze/admin-contracts';
import { Button, EmptyState } from '@planuze/ui';
import { Link } from '@tanstack/react-router';
import { ExternalLink, FileText } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useCompaniesContent } from '../../content/sections/companies.js';
import { fmtDateTime } from '../../lib/format.js';
import { FacetMultiSelect } from '../filters/facet-multi-select.js';

type AuditEntry = CompanyDetail['recentAudit'][number];

interface Props {
  readonly audit: readonly AuditEntry[];
  readonly companyId: string;
}

/* Aba de auditoria da empresa (ADR-0700+). Preview das ~20 entradas recentes carregadas via
   useCompanyDetail; a faceta de tipo de ação é um filtro client-side (useMemo) sobre esse array.
   A trilha completa (paginada por cursor) fica na tela `/audit`, alcançada pelo deep-link
   "Ver auditoria completa" já escopado pelo alvo (`targetType=company` + `targetId`, ADR-0713). */
export function CompanyAuditTab({ audit, companyId }: Props) {
  const copy = useCompaniesContent();
  const [actions, setActions] = useState<string[]>([]);

  const fullAuditLink = (
    <Button asChild variant="outline" size="sm">
      <Link to="/audit" search={{ targetType: 'company', targetId: companyId }}>
        {copy.auditTab.viewFullAudit}
        <ExternalLink className="size-4" aria-hidden="true" />
      </Link>
    </Button>
  );

  /* Tipos de ação deduplicados a partir das entradas carregadas. */
  const actionOptions = useMemo(
    () =>
      [...new Set(audit.map((entry) => entry.action))]
        .sort((a, b) => a.localeCompare(b))
        .map((action) => ({ value: action, label: action })),
    [audit],
  );

  const filtered = useMemo(
    () => (actions.length === 0 ? audit : audit.filter((entry) => actions.includes(entry.action))),
    [audit, actions],
  );

  if (audit.length === 0) {
    return (
      <EmptyState
        icon={<FileText className="size-6" />}
        title={copy.auditTab.emptyTitle}
        primaryAction={fullAuditLink}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <FacetMultiSelect
          label={copy.auditTab.actionTypeLabel}
          values={actions}
          onChange={setActions}
          options={actionOptions}
        />
        <span className="text-sm tabular-nums text-muted-foreground">
          {copy.common.countOf(filtered.length, audit.length)}
        </span>
        <div className="ml-auto">{fullAuditLink}</div>
      </div>
      {filtered.length === 0 ? (
        <EmptyState
          icon={<FileText className="size-6" />}
          title={copy.common.noResultsTitle}
          description={copy.auditTab.emptyFilteredDescription}
          primaryAction={
            <Button variant="outline" onClick={() => setActions([])}>
              {copy.common.clearFilters}
            </Button>
          }
        />
      ) : (
        <ul className="space-y-2">
          {filtered.map((entry, index) => (
            <li
              key={`${entry.ts}-${index}`}
              className="flex items-center justify-between rounded-md border border-border bg-card p-3"
            >
              <div className="flex items-baseline gap-3">
                <span className="font-mono text-sm text-muted-foreground">{entry.action}</span>
                <span className="text-sm text-foreground">{entry.actorEmail ?? entry.actorId}</span>
              </div>
              <span className="text-xs text-muted-foreground">{fmtDateTime(entry.ts)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
